import React, { useState, useEffect } from "react";
import { X, Sparkles, Loader2, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { timeUntil } from "@/lib/paywall";
import { starterScansPhrase, starterPitch, planNumbers } from "@/lib/planCopy";

const TID = {
  paywallDialog: "paywall-dialog",
  paywallStripeBtn: "paywall-stripe-btn",
  paywallRazorpayBtn: "paywall-razorpay-btn",
  paywallClose: "paywall-close-btn",
};

// A function, not a module-level const: the numbers can be corrected by
// `hydrateFromCatalog` after the catalogue loads, and a const evaluated at
// import time would freeze the pre-fetch fallback values forever.
const starterFeatures = () => [
  `${planNumbers().starterScans} scans every month`,
  `Up to ${planNumbers().starterKeywords} keywords per scan`,
  "Open-source links + saved leads",
  "Cancel any time",
];

export default function PaywallModal({ open, info, onClose }) {
  const [stripeLoading, setStripeLoading] = useState(false);
  const [razorpayLoading, setRazorpayLoading] = useState(false);
  const [countdown, setCountdown] = useState("");
  const { refreshWorkspace } = useAuth();

  useEffect(() => {
    if (!info?.nextReset) return;
    const tick = () => setCountdown(timeUntil(info.nextReset));
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [info]);

  if (!open) return null;

  // A paid user can reach this modal (monthly allowance exhausted), and the
  // upgrade framing is wrong for them — they already bought the plan.
  const alreadyPaid = !!info?.plan && info.plan !== "free";

  // USD → Stripe hosted subscription checkout (redirect).
  const startStripe = async () => {
    setStripeLoading(true);
    try {
      const resp = await api.post("/payments/stripe/subscribe", {
        plan_key: "starter_monthly",
        origin_url: window.location.origin,
      });
      const url = resp.data?.url;
      if (!url) throw new Error("No checkout URL returned");
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Stripe checkout.");
      setStripeLoading(false);
    }
  };

  // INR → Razorpay subscription modal + verify.
  const startRazorpay = async () => {
    if (!window.Razorpay) {
      toast.error("Razorpay checkout is still loading — try again in a moment.");
      return;
    }
    setRazorpayLoading(true);
    try {
      const resp = await api.post("/payments/razorpay/subscribe", {
        plan_key: "starter_monthly",
      });
      const opts = resp.data;
      const rzp = new window.Razorpay({
        key: opts.razorpay_key_id,
        subscription_id: opts.subscription_id,
        name: `Leadmato — ${opts.plan_name}`,
        description: "Monthly subscription",
        theme: { color: "#0F5535" },
        modal: { ondismiss: () => setRazorpayLoading(false) },
        handler: async (rzpResp) => {
          try {
            await api.post("/payments/razorpay/subscription/verify", {
              razorpay_subscription_id: rzpResp.razorpay_subscription_id,
              razorpay_payment_id: rzpResp.razorpay_payment_id,
              razorpay_signature: rzpResp.razorpay_signature,
            });
            // Was `window.location.reload()`. The whole point of the workspace
            // refresh in AuthContext is that entitlements re-read WITHOUT a
            // reload; a reload here just hides whether that path actually works,
            // and would keep hiding it once the Phase-5 epoch lands. Await the
            // refresh so the modal only closes once the new plan is really in
            // context — closing first would flash the old free-tier gates back.
            await refreshWorkspace().catch(() => {});
            toast.success("🎉 You're on Starter — everything's unlocked.");
            onClose?.();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Subscription verification failed.");
          } finally {
            setRazorpayLoading(false);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed.");
        setRazorpayLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Razorpay checkout.");
      setRazorpayLoading(false);
    }
  };

  return (
    <div
      data-testid={TID.paywallDialog}
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/45 lm-fade-in" onClick={onClose} />
      <div className="relative bg-lm-surface border border-lm-border rounded-2xl w-full max-w-lg shadow-[0_20px_50px_-10px_rgba(0,0,0,0.3)] lm-fade-in overflow-hidden">
        <button
          data-testid={TID.paywallClose}
          className="absolute top-4 right-4 text-lm-text3 hover:text-lm-text z-10"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="p-7 pb-5 bg-gradient-to-br from-lm-tint3/50 to-lm-surface">
          <div className="w-12 h-12 rounded-xl bg-lm-secondary text-white flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6" strokeWidth={1.7} />
          </div>
          <h2 className="text-2xl font-display font-semibold leading-tight mb-1">
            {alreadyPaid ? "You're out of scans for now" : "Upgrade to Starter"}
          </h2>
          <p className="text-lm-text2 leading-relaxed text-sm">
            {/* The server knows WHICH limit was hit and when it lifts; this used
                to render a hardcoded free-tier line, which told paying users to
                buy the plan they were already on. Trust the server's wording. */}
            {info?.message || `You've used your free scan. ${starterPitch()}`}
          </p>
        </div>

        {/* Plan card */}
        <div className="px-7 pb-2">
          <div className="rounded-xl border border-lm-primary bg-lm-tint2/40 p-5 relative">
            <div className="absolute top-3 right-4 text-[10px] font-mono uppercase tracking-widest bg-lm-secondary text-white px-2 py-1 rounded">
              monthly
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-lm-primary mb-1">
              Starter
            </div>
            <div className="text-3xl font-display font-semibold mb-1">
              ₹1,999 <span className="text-base text-lm-text2 font-normal">/ $19.99 per month</span>
            </div>
            <div className="text-xs text-lm-text2 mb-4 font-mono">{starterScansPhrase()} · {planNumbers().starterKeywords} keywords each · 1 brand</div>
            <ul className="space-y-1.5 mb-2">
              {starterFeatures().map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-lm-text">
                  <Check className="w-3.5 h-3.5 text-lm-primary shrink-0" strokeWidth={2.5} />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="p-7 pt-4 flex flex-col gap-3">
          <button
            data-testid={TID.paywallRazorpayBtn}
            onClick={startRazorpay}
            disabled={razorpayLoading || stripeLoading}
            className="w-full inline-flex items-center justify-center gap-2 bg-lm-primary hover:bg-lm-primaryHover text-white rounded-lg px-5 py-3.5 font-semibold transition-colors disabled:opacity-60"
          >
            {razorpayLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Upgrade for ₹1,999/mo (Razorpay)
          </button>
          <button
            data-testid={TID.paywallStripeBtn}
            onClick={startStripe}
            disabled={razorpayLoading || stripeLoading}
            className="w-full inline-flex items-center justify-center gap-2 border border-lm-border bg-lm-surface hover:bg-lm-tint rounded-lg px-5 py-3.5 font-medium transition-colors disabled:opacity-60"
          >
            {stripeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Pay $19.99/mo with card (Stripe)
          </button>
          <button
            onClick={onClose}
            className="text-sm text-lm-text3 hover:text-lm-text2 mt-1 inline-flex items-center justify-center gap-1"
          >
            <Clock className="w-3.5 h-3.5" />
            {countdown ? `Maybe later (resets in ${countdown})` : "Maybe later"}
          </button>
        </div>
      </div>
    </div>
  );
}
